<script setup lang="ts">
import { computed, ref, toRaw } from 'vue'
import type {
  DocumentAssoc,
  DocumentSign,
  DocumentSignFile,
  DocumentSignSignersFormData,
  DocumentSignSignersFormDef,
} from '../../../../Types/document'
import type { FieldConfig } from '../../../../Types/form'
import type { FormCreatorInstance } from '../UI/Form/formCreator'
import FormCreator from '../UI/Form/FormCreator.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import PlaceholderContent from '../UI/Loading/PlaceholderContent.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useToast from '../../Composables/useToast'
import { useI18n } from 'vue-i18n'
import { useDocumentSignApi } from '../../../../Composables/DocumentSign/useDocumentSignApi'
import DocumentDownloadButton from './DocumentDownloadButton.vue'
import DocumentDropbox from './DocumentDropbox.vue'
import DocumentShowAction from './DocumentShowAction.vue'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import useModal from '../../../../Composables/useModal.ts'

interface Props {
  documentSign: DocumentSign
  disabled: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'sign-cancelled': []
  'signers-updated': []
  'document-sign-removed': []
  'document-sign-file-replaced': [{ documentSignFile: DocumentSignFile; documentAssoc: DocumentAssoc }]
}>()

const { addSuccess } = useToast()
const { t } = useI18n()
const { cancel, deleteDocument } = useDocumentSignApi()
const formRef = computed(() => `documentSignForm-${props.documentSign.id}`)
const formCreatorName = computed(() => `documentSignForm-${props.documentSign.id}`)
const canModify = computed(() => props.documentSign.isSentToProvider && props.documentSign.isCancelable)
const endpoint = computed(() => `document-signs/${props.documentSign.id}/signers`)

const formCreatorRef = ref<FormCreatorInstance | null>(null)
const loadField = ref(false)
const formData = ref<DocumentSignSignersFormData>({})
const def = ref<DocumentSignSignersFormDef>({})
const sending = ref<boolean>(false)
const deleting = ref<boolean>(false)

const {
  modal: cancelConfirmationModal,
  open: openConfirmationModal,
  close: closeConfirmationModal,
} = useModal({
  prefix: 'document-sign',
})

const hasDocuments = computed<boolean>(() => Object.keys(defSigners.value).length > 0)

const defSigners = computed<Record<string, string[] | undefined>>(() => {
  return Object.entries(def.value).reduce(
    (acc, [documentSignerKey, documentSignFileSigners]) => {
      const fieldConfig = documentSignFileSigners as unknown as Record<
        string,
        { options?: { fieldOrderPriority?: number } }
      >
      acc[documentSignerKey] = Object.keys(fieldConfig).sort((a, b) => {
        const priorityA = fieldConfig[a]?.options?.fieldOrderPriority ?? 0

        const priorityB = fieldConfig[b]?.options?.fieldOrderPriority ?? 0

        return priorityA - priorityB
      })

      return acc
    },
    {} as Record<string, string[] | undefined>,
  )
})

const getSignersForFile = (fileId: number): string[] => {
  return defSigners.value[`signers_${fileId}`] ?? []
}

function updateData(newData: DocumentSignSignersFormData): void {
  formData.value = newData
}

function setBuild(bool: boolean): void {
  loadField.value = bool
}

function setElement(formDef: DocumentSignSignersFormDef): void {
  def.value = formDef
}

function synchronizeSignerOptions(field: string, signerType: string) {
  if (!def.value) {
    return
  }

  const [signerGroup] = field.split('.')
  if (!signerGroup) {
    console.warn('Field group cannot be determined')
    return
  }

  const fieldGroup: Record<string, FieldConfig> | null = def.value[signerGroup] ?? null
  if (fieldGroup === null) {
    console.warn('Field group not found')
    return
  }

  const fieldDef: FieldConfig | null = fieldGroup[signerType] ?? null
  if (fieldDef === null) {
    console.warn('Field def not found')
    return
  }

  const upToDateOptions = fieldDef.options.choices ?? []

  for (const group in def.value) {
    if (group === signerGroup || !def.value[group]) {
      continue
    }

    const field = def.value[group][signerType] as FieldConfig | null
    if (field !== null) {
      field.options.choices = upToDateOptions
    }
  }

  emit('signers-updated')
}

async function cancelSign(): Promise<void> {
  try {
    const documentSignResponse = await cancel(props.documentSign.id)
    addSuccess(t('contract.document.documentSign.cancelSignSuccess'))

    if (documentSignResponse.id !== props.documentSign.id) {
      const redirect = new URL(window.location.href)
      redirect.searchParams.set('documentSignId', documentSignResponse.id.toString())
      window.location.href = redirect.toString()
      return
    }
    emit('sign-cancelled')
  } finally {
    closeConfirmationModal()
  }
}

async function removeDocumentSignFile(documentSignFileId: number): Promise<void> {
  deleting.value = true
  try {
    await deleteDocument(props.documentSign.id, documentSignFileId)
    addSuccess(t('contract.document.documentSign.cancelSignSuccess'))
    emit('document-sign-removed')
  } finally {
    deleting.value = false
  }
}

async function send(): Promise<boolean> {
  if (!formCreatorRef.value) {
    return false
  }

  sending.value = true

  try {
    await formCreatorRef.value.methods('patch')
    emit('signers-updated')
    return true
  } finally {
    sending.value = false
  }
}

function onDocumentReplaced(documentSignFile: DocumentSignFile, newDocumentAssoc: DocumentAssoc): void {
  emit('document-sign-file-replaced', { documentSignFile, documentAssoc: newDocumentAssoc })
}
</script>

<template>
  <div>
    <FormCreator
      ref="formCreatorRef"
      class-to-add="d-none"
      :params="{ _z: 1 }"
      :endpoint="endpoint"
      :src-error="formCreatorName"
      :disabled="props.disabled"
      @update-data="updateData"
      @form-build="setBuild"
      @element-build="setElement"
    />
    <div
      v-for="documentSignFile in documentSign.documentSignFiles"
      :key="`${documentSignFile.document.name}-${documentSignFile.id}`"
      class="p-3 mb-3 border border-blue rounded-xl"
    >
      <div class="font-weight-bold mb-4 d-flex justify-content-between align-items-center">
        {{ documentSignFile.document.documentType.name }}
        <div class="d-flex">
          <template v-if="documentSignFile.documentAssoc">
            <DocumentDropbox
              v-if="!disabled"
              update="modify"
              :document="documentSignFile.documentAssoc"
              @document-uploaded="
                ($event: any, payload: any, newDocumentAssoc: DocumentAssoc) =>
                  onDocumentReplaced(documentSignFile, newDocumentAssoc)
              "
            />
            <DocumentShowAction :document="documentSignFile.documentAssoc" class="ml-2" />
            <DocumentDownloadButton :document="documentSignFile.documentAssoc" have-btn class="ml-2" />
          </template>
          <ButtonAll
            v-if="!disabled"
            margin="no"
            type="danger"
            icon="trash"
            class="ml-2"
            :loading="deleting"
            @click="removeDocumentSignFile(documentSignFile.id)"
          />
        </div>
      </div>

      <div v-if="loadField && getSignersForFile(documentSignFile.id).length > 0" class="signers-form">
        <FieldCreator
          v-for="signerType in getSignersForFile(documentSignFile.id)"
          :key="`signer-${documentSignFile.id}-${signerType}`"
          add-user
          dynamic-label
          label-left-equal
          :form-data="formData"
          :name="`signers_${documentSignFile.id}.${signerType}`"
          :form-ref="formRef"
          :disabled="props.disabled"
          :data-test-id="`signer-${documentSignFile.id}-${signerType}`"
          @options-changed="synchronizeSignerOptions(`signers_${documentSignFile.id}.${signerType}`, signerType)"
        />
      </div>
      <div v-else-if="!loadField" class="loader-container">
        <PlaceholderContent :rows="3" />
      </div>
    </div>
    <div class="d-flex justify-content-end">
      <ButtonAll
        v-if="!props.disabled"
        type="validation"
        margin="no"
        class="mt-2 align-self-end"
        label="button.validate"
        :loading="sending"
        :disabled="!hasDocuments"
        @click="send"
      />
      <ButtonAll
        v-if="canModify"
        type="warning"
        margin="no"
        class="mt-2 align-self-end"
        label="contract.document.documentSign.cancelSign"
        @click="openConfirmationModal"
      />

      <ModalConfirmation
        id="cancelConfirmationModal"
        ref="cancelConfirmationModal"
        message="contract.document.documentSign.cancelSign"
        title="contract.document.documentSign.cancelSignConfirm"
        button-confirm-title="contract.document.documentSign.cancelSign"
        button-cancel-title="main.bool.false"
        :loader="true"
        @confirm="cancelSign"
      />
    </div>
  </div>
</template>
